// @ts-nocheck

import { MD5 } from 'crypto-js';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { ButtonUI, CustomThemeProvider, Toast, usePortal } from 'sonata-react-components';

import { copyToClipboard } from '@/helpers/reporting.helpers';
import { getCampaigns } from '@/store/selectors/reportingAdvancedFilter.selectors';
import { createSharedReport, getSharedReportPermissions, updateShareReportPassword } from '../../reporting.resources';
import { ReportingShareReportLink } from '../ReportingShareReportLink';
import { ReportingShareReportModalContext } from '../ReportingShareReportModalContext';
import { ReportingShareReportPassword } from '../ReportingShareReportPassword';
import { ReportingShareSimpleReportModalStyle } from './reportingShareSimpleReportModal.style';

function ReportingShareSimpleReportModal({ buttonProps, sharedReportCallback, sharedReportId, sharedReportPassword }) {
  const { formatMessage } = useIntl();

  const [state, setState] = useState({
    currentSharedReportId: sharedReportId,
    currentSharedReportLink: `${taptap.config.sonataReportingAppUrl}?report=${sharedReportId || 'XXXXXX'}`,
    currentSharedReportPassword: sharedReportPassword,
    loading: false,
    modalRef: useRef(),
    passwordHasBeenSaved: !!sharedReportPassword,
    passwordLinkEnabled: !!sharedReportId,
    passwordNeedsUpdate: false,
    passwordValid: !!sharedReportPassword,
    sharedReportPermissions: [],
    showErrorToast: false,
    showLinkToast: false,
    showPasswordToast: false
  });

  const {
    currentSharedReportId,
    currentSharedReportLink,
    currentSharedReportPassword,
    loading,
    modalRef,
    passwordHasBeenSaved,
    passwordLinkEnabled,
    passwordNeedsUpdate,
    passwordValid,
    sharedReportPermissions,
    showErrorToast,
    showLinkToast,
    showPasswordToast
  } = state;

  const campaignIds = useSelector(getCampaigns);

  useEffect(() => {
    (async () => {
      const existingShareReportPermissions = await getSharedReportPermissions();
      setState(prevState => ({
        ...prevState,
        sharedReportPermissions: existingShareReportPermissions
      }));
    })();
  }, []);

  const passwordGenerateCallback = useCallback(async () => {
    setState(prevState => ({
      ...prevState,
      loading: true
    }));
    const passwordHashed = MD5(currentSharedReportPassword).toString();
    let newSharedReportId = currentSharedReportId;
    try {
      if (currentSharedReportId) {
        await updateShareReportPassword(currentSharedReportId, passwordHashed);
      } else {
        const sharedReportPermissionsId = sharedReportPermissions.find(sRP => sRP.isDefault)._id;
        const newSharedReport = await createSharedReport(passwordHashed, campaignIds, sharedReportPermissionsId);
        newSharedReportId = newSharedReport._id;
      }
      setState(prevState => ({
        ...prevState,
        currentSharedReportId: newSharedReportId,
        currentSharedReportLink: `${taptap.config.sonataReportingAppUrl}?report=${newSharedReportId}`,
        loading: false,
        passwordHasBeenSaved: true,
        passwordLinkEnabled: true,
        passwordNeedsUpdate: false,
        showPasswordToast: false
      }));
    } catch {
      setState(prevState => ({
        ...prevState,
        loading: false,
        passwordHasBeenSaved: true,
        showErrorToast: true,
        showPasswordToast: false
      }));
    }
  }, [currentSharedReportPassword, currentSharedReportId]);

  const passwordCopyToClipboardCallback = useCallback(() => {
    copyToClipboard(currentSharedReportLink);
    setState(prevState => ({
      ...prevState,
      showLinkToast: true
    }));
  }, [currentSharedReportLink]);

  const closeModalCallback = useCallback(() => {
    sharedReportCallback(currentSharedReportId, currentSharedReportPassword);
  }, [passwordValid, currentSharedReportPassword, currentSharedReportId, modalRef, sharedReportCallback]);

  const onCloseToast = () => {
    setState(prevState => ({
      ...prevState,
      showErrorToast: false,
      showLinkToast: false,
      showPasswordToast: false
    }));
  };

  const targetBody = usePortal('sharedReportModalContainer');

  const sharedReportModal = useCallback(() => {
    return ReactDOM.createPortal(
      <ReportingShareReportModalContext.Provider
        value={{
          currentSharedReportId,
          currentSharedReportLink,
          currentSharedReportPassword,
          loading,
          passwordHasBeenSaved,
          passwordLinkEnabled,
          passwordNeedsUpdate,
          passwordValid,
          setContext: setState,
          sharedReportPermissions,
          showLinkToast,
          showPasswordToast
        }}
      >
        <CustomThemeProvider themeContrast="light">
          <ReportingShareSimpleReportModalStyle
            centeredTitleDescription
            className="sharedReportModal"
            closeModalCallback={closeModalCallback}
            description={formatMessage({
              defaultMessage: 'Share your report with others partners',
              id: 'reporting.shareReportModal.description'
            })}
            ref={modalRef}
            title={formatMessage({
              defaultMessage: 'Share Report',
              id: 'reporting.shareReportModal.title'
            })}
            titleIcon="far fa-link"
            width="800px"
          >
            <div className="sharedReportModal__container">
              {(showPasswordToast || showLinkToast || showErrorToast) && (
                <Toast
                  autoClose={false}
                  className="sharedReportModal__messagesToast"
                  closeButton
                  closeToast={onCloseToast}
                  icon={`fal fa-${showLinkToast ? 'check-circle' : 'exclamation-triangle'}`}
                  text={formatMessage({
                    defaultMessage: showErrorToast
                      ? "The link couldn't be generated, please try again"
                      : showPasswordToast
                        ? "You've changed the password, update the link please"
                        : 'The link has been copied correctly',
                    id: `reporting.shareReportModal.${showErrorToast ? 'error' : showPasswordToast ? 'password' : 'link'}Toast`
                  })}
                  type={`${showErrorToast ? 'danger' : showPasswordToast ? 'warning' : 'success'}`}
                />
              )}
              <ReportingShareReportPassword passwordGenerateCallback={passwordGenerateCallback} />
              <ReportingShareReportLink passwordCopyToClipboardCallback={passwordCopyToClipboardCallback} />
            </div>
          </ReportingShareSimpleReportModalStyle>
        </CustomThemeProvider>
      </ReportingShareReportModalContext.Provider>,
      targetBody
    );
  }, [
    campaignIds,
    currentSharedReportId,
    currentSharedReportPassword,
    passwordCopyToClipboardCallback,
    passwordGenerateCallback,
    passwordHasBeenSaved,
    passwordLinkEnabled,
    passwordNeedsUpdate,
    passwordValid,
    sharedReportPermissions,
    showLinkToast,
    showPasswordToast
  ]);

  const showSharedReportModal = useCallback(() => {
    modalRef && modalRef.current && modalRef.current.openModal();
  }, [modalRef]);

  return (
    <>
      <ButtonUI {...buttonProps} onClick={showSharedReportModal} />
      {sharedReportModal()}
    </>
  );
}

ReportingShareSimpleReportModal.propTypes = {
  buttonProps: PropTypes.object.isRequired,
  sharedReportCallback: PropTypes.func.isRequired,
  sharedReportId: PropTypes.string,
  sharedReportPassword: PropTypes.string
};

export default React.memo(ReportingShareSimpleReportModal);
